import {useConfig} from '@aboutyou/config';
import {useTranslation} from '@aboutyou/i18n';
import {
  ContentPopperProps,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  SecondaryContentPopperButton,
  Text,
  mobileOnlyLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useScayleShippingState} from '../../scayle-state/useScayleShippingState';
import {
  SustainabilityBadgeSlot,
  SustainabilityBadgeSlots,
} from './SustainabilityBadgeSlot';
import {SustainabilityBadgeStyles} from './sustainabilityBadgeStyles';

export interface SustainabilitySlotsProps {
  SustainabilityInfoLayoutComponent: ContentPopperProps['LayoutComponent'];
}

export const SustainabilitySlots: React.FC<SustainabilitySlotsProps> = ({
  SustainabilityInfoLayoutComponent,
}) => {
  const [openPopup, setOpenPopup] = React.useState(false);
  const {t} = useTranslation();
  const isMobile = useConfig().device === 'mobile';
  const {homeDeliveryFee} = useScayleShippingState();

  const testId = 'sustainabilityBadge';
  const lessEmissionsText = t('Less CO<sub>2</sub> emissions');
  const collectionPointFreeText = t('Free');

  const collectionPointBadgeText = homeDeliveryFee
    ? collectionPointFreeText
    : lessEmissionsText;

  const leafIcon = (
    <Icon
      data-testid={`${testId}-infoButton`}
      asset={GenericIconName.SustainabilityLeaf}
      fill="#416000"
      size="XS"
    />
  );

  const handleOpenModal = () => {
    setOpenPopup(true);
  };

  const handleOnCloseModal = () => {
    setOpenPopup(false);
  };

  return (
    <>
      <SustainabilityBadgeStyles />
      <SustainabilityBadgeSlot
        slotName={SustainabilityBadgeSlots.slotSuggestCollectionPoint}
        classNames="wrapper"
        badgeText={isMobile ? undefined : lessEmissionsText}
        testId={testId}
        infoIcon={
          !isMobile && (
            <InfoIcon
              size="SS"
              handleOpenModal={handleOpenModal}
              testId={testId}
            />
          )
        }
        leafIcon={leafIcon}
        onBadgeClick={handleOpenModal}
      />
      <SustainabilityBadgeSlot
        slotName={SustainabilityBadgeSlots.slotOptionCollectionPoint}
        classNames={
          homeDeliveryFee ? 'wrapper banner noIcon' : 'wrapper banner'
        }
        badgeText={collectionPointBadgeText}
        testId={testId}
        onBadgeClick={
          !homeDeliveryFee && !isMobile ? handleOpenModal : undefined
        }
        infoIcon={
          homeDeliveryFee ? undefined : (
            <InfoIcon
              size="S"
              handleOpenModal={handleOpenModal}
              testId={testId}
            />
          )
        }
        leafIcon={homeDeliveryFee ? undefined : leafIcon}
      />
      {homeDeliveryFee && (
        <SustainabilityBadgeSlot
          slotName={SustainabilityBadgeSlots.slotOptionHomeDelivery}
          classNames="wrapper banner greyBG noIcon"
          badgeText={homeDeliveryFee}
          testId={testId}
        />
      )}
      <SustainabilityInfoLayoutComponent
        data-testid={`${testId}-modal`}
        onClose={handleOnCloseModal}
        open={openPopup}
        headerContent={
          <Header
            dangerouslySetInnerHTML={{
              __html: lessEmissionsText,
            }}
          />
        }
        bodyContent={
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                'Our delivery to a post office, parcel shop or parcel locker emits fewer carbon dioxide (CO<sub>2</sub>) emissions than home delivery. To reduce personal CO<sub>2</sub> emissions, we recommend picking up your delivery by public transportation, bicycle or on foot or while you are running your errands.',
              ),
            }}
          />
        }
        footerContent={
          <SecondaryContentPopperButton
            label={t('Close')}
            onClick={handleOnCloseModal}
          />
        }
        renderInPortal
      />
    </>
  );
};

const Header = styled(Text)`
  ${mobileOnlyLinaria(`
    font-size: ${FONT_SIZES.px20};
    font-weight: ${FONT_WEIGHTS.bold};
    margin-top: 5px;
  `)}
`;

const InfoIcon: React.FC<{
  size: 'S' | 'SS';
  handleOpenModal: VoidFunction;
  testId: string;
}> = ({size, handleOpenModal, testId}) => (
  <Icon
    className="info-svg"
    data-testid={`${testId}-infoButton`}
    asset={GenericIconName.InfoOutline}
    fill="#416000"
    size={size}
    onClick={e => {
      e.stopPropagation();
      handleOpenModal?.();
    }}
  />
);
